/* eslint-disable max-lines-per-function */
import {
  FiltersToolbarRoot,
  FiltersToolbarWrapper,
  FiltersToolbarContainer,
  FiltersToolbarFilters,
  FiltersToolbarMore,
  FiltersToolbarMoreTrigger,
  FiltersToolbarMoreContent,
  FiltersToolbarMoreContentFilters,
  FiltersToolbarLeftActions,
  FiltersToolbarMoreContentHeader,
  FiltersToolbarMoreContentFooter,
  FiltersToolbarRightActions,
} from '@hqo/filters-toolbar';
import { Button } from '@hqo/ui-atoms';
import { SlidersHorizontal } from 'lucide-react';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';

import {
  isCompanyFilterEnabledSelector,
  isHostFilterEnabledSelector,
  isSearchEnabledSelector,
  isStatusFilterEnabledSelector,
} from 'store/ui-metadata/selectors';
import { clearFilters } from 'store/visits/actions';
import { CompanyFilter } from 'components/v2/filters/company-filter';
import { FiltersToolbarPinButton } from 'components/v2/filters/filters-toolbar-pin-button';
import { FiltersToolbarSearchInput } from 'components/v2/filters/filters-toolbar-search-input';
import { HostFilter } from 'components/v2/filters/host-filter';
import { StatusFilter } from 'components/v2/filters/status-filter';

export const FiltersToolbar = (): JSX.Element => {
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();

  const filtersLabel = formatMessage({ id: 'labels.filtersToolbar.filtersLabel', defaultMessage: 'Filters' });
  const showSearchFilter = useSelector(isSearchEnabledSelector);
  const showStatusFilter = useSelector(isStatusFilterEnabledSelector);
  const showHostFilter = useSelector(isHostFilterEnabledSelector);
  const showCompanyFilter = useSelector(isCompanyFilterEnabledSelector);

  const handleClearAll = useCallback(() => {
    dispatch(clearFilters());
  }, [dispatch]);

  return (
    <div>
      <FiltersToolbarRoot mobileBreakpoint={640} className="w-full min-w-0 p-0 relative">
        <style>
          {`
        /* Example: Style hidden filters in popover using CSS selectors */
        .filters-toolbar-more-content-filters-list {
          display: flex;
          flex-direction: column;
          gap: 0.75rem; /* space-y-3 */
        }

        .filters-toolbar-more-content-filter-item {
          width: 100%;
        }

        .filters-toolbar-more-content-filter-item button,
        .filters-toolbar-more-content-filter-item [role='combobox'] {
          width: 100%;
        }

        /* Prevent absolutely positioned hidden filters from causing horizontal scroll */
        .shrink-0.absolute.translate-x-full {
          clip-path: inset(0 0 0 0) !important;
        }
`}
        </style>
        {showSearchFilter && <FiltersToolbarSearchInput />}

        <FiltersToolbarWrapper className="min-w-0 -mr-2 relative overflow-x-clip max-w-full">
          <FiltersToolbarContainer className="min-w-0 relative overflow-x-clip max-w-full">
            <FiltersToolbarLeftActions>
              <FiltersToolbarPinButton />
            </FiltersToolbarLeftActions>

            <FiltersToolbarFilters className="min-w-0 relative overflow-x-clip max-w-full">
              {showHostFilter && <HostFilter />}
              {showCompanyFilter && <CompanyFilter />}
              {showStatusFilter && <StatusFilter />}
            </FiltersToolbarFilters>

            <FiltersToolbarMore>
              <FiltersToolbarMoreTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2">
                  <SlidersHorizontal className="size-4" />
                  <span className="hidden md:inline">{filtersLabel}</span>
                </Button>
              </FiltersToolbarMoreTrigger>
              <FiltersToolbarMoreContent className="w-full sm:w-80 rounded-t-[16px]">
                <div className="p-3 sm:p-0 h-[60vh] sm:h-auto flex flex-col ">
                  <FiltersToolbarMoreContentHeader className="flex flex-col space-y-2 sm:text-left pb-4">
                    <h2 className="font-medium text-system-black text-center text-sm">{filtersLabel}</h2>
                  </FiltersToolbarMoreContentHeader>
                  <FiltersToolbarMoreContentFilters className="flex-1 overflow-y-auto" />
                  <FiltersToolbarMoreContentFooter>
                    <Button
                      className="self-start p-0 h-auto mb-2"
                      variant="link"
                      onClick={handleClearAll}
                      data-testid="filters-toolbar-clear-all"
                    >
                      <FormattedMessage id="labels.filtersToolbar.clearButtonLabel" defaultMessage="Clear all" />
                    </Button>
                  </FiltersToolbarMoreContentFooter>
                </div>
              </FiltersToolbarMoreContent>
            </FiltersToolbarMore>

            <FiltersToolbarRightActions>
              <span />
            </FiltersToolbarRightActions>
          </FiltersToolbarContainer>
        </FiltersToolbarWrapper>
      </FiltersToolbarRoot>
    </div>
  );
};
